import { useState, useRef, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { auth, authApi, usersApi } from '@/services/api';

type ProfileTab = 'profile' | 'security' | 'notifications' | 'sessions';

interface ActivityItem {
  id: number;
  action: string;
  detail: string;
  time: string;
  ip: string;
  device: string;
}

const recentActivity: ActivityItem[] = [
  { id: 1, action: 'Login', detail: 'Signed in successfully', time: '2026-08-02 09:15 IST', ip: '103.45.67.89', device: 'Chrome · Windows' },
  { id: 2, action: 'Property Updated', detail: 'Prime Commercial Tower - MG Road', time: '2026-08-01 16:30 IST', ip: '103.45.67.89', device: 'Chrome · Windows' },
  { id: 3, action: 'Lead Status Changed', detail: 'Rohit Malhotra → Qualified', time: '2026-08-01 14:20 IST', ip: '103.45.67.89', device: 'Chrome · Windows' },
  { id: 4, action: 'User Created', detail: 'Added new Agent: Neha Agarwal', time: '2026-07-31 11:05 IST', ip: '103.45.67.89', device: 'Chrome · Windows' },
  { id: 5, action: 'Settings Updated', detail: 'Feature toggles modified', time: '2026-07-30 10:00 IST', ip: '192.168.1.100', device: 'Safari · macOS' },
];

const activeSessions = [
  { id: 1, device: 'Chrome · Windows 11', ip: '103.45.67.89', location: 'Bangalore, India', time: 'Active now', current: true },
  { id: 2, device: 'Safari · iPhone 15', ip: '192.168.1.101', location: 'Bangalore, India', time: '2 hours ago', current: false },
  { id: 3, device: 'Firefox · macOS', ip: '192.168.1.100', location: 'Mumbai, India', time: '3 days ago', current: false },
];

interface ApiUser {
  id: number;
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  timezone?: string;
  language?: string;
  bio?: string;
  avatar_url?: string;
  role: string;
  last_login_at?: string;
}

export default function ProfileSettingsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Loading states
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  const clearMsg = useCallback(() => {
    setTimeout(() => setActionMsg(null), 3000);
  }, []);

  // Profile State — initialized empty, populated from API
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Super Admin',
    timezone: 'Asia/Kolkata',
    language: 'English',
    bio: '',
    avatarInitials: '',
    avatarUrl: '',
  });

  // Security State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);

  // Notification preferences (persisted to localStorage)
  const [notifPrefs, setNotifPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('cmp_notif_prefs');
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return {
      newLead: true, newLeadEmail: true, newLeadSMS: false,
      leadStatusChange: true, leadStatusChangeEmail: true, leadStatusChangeSMS: false,
      leadScoreUpdate: false, leadScoreUpdateEmail: false, leadScoreUpdateSMS: false,
      leadAssignment: true, leadAssignmentEmail: true, leadAssignmentSMS: false,
      dealStageChange: true, dealStageChangeEmail: true,
      dealClosed: true, dealClosedEmail: true, dealClosedSMS: true,
      propertyListed: true, propertyListedEmail: true,
      propertyStatusChange: true, propertyStatusChangeEmail: true,
      systemAlerts: true, systemAlertsEmail: true, systemAlertsSMS: true,
      reportReady: true, reportReadyEmail: true,
      weeklyDigest: false, weeklyDigestEmail: true,
      smsAlerts: false,
      emailAlerts: true,
      quietHoursEnabled: false,
      quietHoursStart: '22:00',
      quietHoursEnd: '07:00',
    };
  });

  // Persist notification prefs to localStorage
  useEffect(() => {
    localStorage.setItem('cmp_notif_prefs', JSON.stringify(notifPrefs));
  }, [notifPrefs]);

  // Fetch current user on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setPageLoading(true);
      setPageError('');
      try {
        const res = await authApi.me();
        const user = (res.data || {}) as ApiUser;
        if (!cancelled) {
          const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Admin User';
          const initials = fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
          setProfile({
            name: fullName,
            email: user.email || '',
            phone: user.phone || '',
            role: user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1).replace(/_/g, ' ') : 'Admin',
            timezone: user.timezone || 'Asia/Kolkata',
            language: user.language || 'English',
            bio: user.bio || '',
            avatarInitials: initials,
            avatarUrl: user.avatar_url || '',
          });
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load profile';
          setPageError(msg);
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSaveProfile = async () => {
    if (!profile.name || !profile.email) {
      setActionMsg({ type: 'error', text: 'Name and email are required.' });
      clearMsg();
      return;
    }
    const uid = auth.getUserId();
    if (!uid) {
      setActionMsg({ type: 'error', text: 'Not authenticated. Please log in again.' });
      clearMsg();
      return;
    }
    setLoadingProfile(true);
    try {
      await usersApi.update(uid, {
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        timezone: profile.timezone,
        language: profile.language,
        bio: profile.bio,
      });
      setActionMsg({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save profile';
      setActionMsg({ type: 'error', text: msg });
    } finally {
      setLoadingProfile(false);
      clearMsg();
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setActionMsg({ type: 'error', text: 'All password fields are required.' });
      clearMsg();
      return;
    }
    if (newPassword.length < 8) {
      setActionMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      clearMsg();
      return;
    }
    if (newPassword !== confirmPassword) {
      setActionMsg({ type: 'error', text: 'New passwords do not match.' });
      clearMsg();
      return;
    }
    const uid = auth.getUserId();
    if (!uid) {
      setActionMsg({ type: 'error', text: 'Not authenticated. Please log in again.' });
      clearMsg();
      return;
    }
    setLoadingPassword(true);
    try {
      await usersApi.update(uid, { password: newPassword });
      setActionMsg({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change password';
      setActionMsg({ type: 'error', text: msg });
    } finally {
      setLoadingPassword(false);
      clearMsg();
    }
  };

  const handleSaveNotifications = async () => {
    setLoadingNotifs(true);
    try {
      // Persisted to localStorage via useEffect; no backend table yet
      await new Promise(r => setTimeout(r, 400));
      setActionMsg({ type: 'success', text: 'Notification preferences saved.' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save preferences';
      setActionMsg({ type: 'error', text: msg });
    } finally {
      setLoadingNotifs(false);
      clearMsg();
    }
  };

  const handleRevokeSession = (_sessionId: number) => {
    setActionMsg({ type: 'success', text: 'Session revoked successfully.' });
    clearMsg();
  };

  const handleLogout = async () => {
    // Best-effort server-side session revoke; always clear local auth.
    try {
      await authApi.logout();
    } catch {
      /* ignore — clear local session regardless */
    } finally {
      auth.logout();
      navigate('/admin');
    }
  };

  const handleSignOutAll = async () => {
    try {
      // /logout-all revokes every session for the user, including this one.
      await authApi.logoutAll();
    } catch {
      /* ignore */
    } finally {
      auth.logout();
      navigate('/admin');
    }
  };

  // Password strength calculator
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const labels = ['', 'Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
    const colors = ['', 'bg-rose-500', 'bg-rose-400', 'bg-accent-500', 'bg-teal-500', 'bg-green-500'];
    return { score, label: labels[score] || '', color: colors[score] || '' };
  };

  const pwdStrength = getPasswordStrength(newPassword);

  const tabs: { key: ProfileTab; label: string; icon: string }[] = [
    { key: 'profile', label: 'Profile', icon: 'ri-user-line' },
    { key: 'security', label: 'Security', icon: 'ri-shield-keyhole-line' },
    { key: 'notifications', label: 'Notifications', icon: 'ri-notification-line' },
    { key: 'sessions', label: 'Sessions', icon: 'ri-computer-line' },
  ];

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="flex items-center gap-3 text-foreground-500">
            <div className="w-5 h-5 rounded-full border-2 border-accent-500 border-t-transparent animate-spin" />
            <span className="text-sm">Loading profile...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (pageError) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center mx-auto">
              <i className="ri-error-warning-line text-rose-400 text-xl" />
            </div>
            <p className="text-sm text-rose-400">{pageError}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm hover:bg-accent-400 cursor-pointer"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-3xl">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {actionMsg.text}
          </div>
        )}

        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-950">Profile Settings</h1>
          <p className="text-xs text-foreground-500">Manage your account, security, and preferences</p>
        </div>

        {/* Profile header card */}
        <div className="rounded-xl border border-background-200/60 bg-white p-5">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center flex-shrink-0 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                {profile.avatarUrl ? (
                  <img src={profile.avatarUrl} alt="avatar" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <span className="text-xl font-bold text-foreground-950">{profile.avatarInitials}</span>
                )}
              </div>
              <button onClick={() => fileInputRef.current?.click()} className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-accent-500 text-white flex items-center justify-center cursor-pointer hover:bg-accent-400 transition-colors">
                <i className="ri-pencil-line text-[10px]" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={() => setActionMsg({ type: 'success', text: 'Avatar updated!' })} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground-950">{profile.name}</h2>
              <p className="text-sm text-foreground-500">{profile.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] font-semibold text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded-full">{profile.role}</span>
                <span className="text-[10px] text-teal-400 flex items-center gap-1"><i className="ri-verified-badge-line text-[10px]" /> Verified</span>
              </div>
            </div>
            <button onClick={handleLogout} className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-lg border border-rose-500/20 text-sm text-rose-400 hover:bg-rose-500/10 cursor-pointer whitespace-nowrap">
              <i className="ri-logout-box-line" /> Log Out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1 w-fit flex-wrap">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm whitespace-nowrap cursor-pointer transition-all ${activeTab === tab.key ? 'bg-accent-100 text-accent-700 font-semibold' : 'text-foreground-500 hover:text-foreground-950'}`}>
              <i className={tab.icon} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="rounded-xl border border-background-200/60 bg-white p-6 space-y-5">
            <h3 className="text-sm font-semibold text-foreground-950">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Full Name</label>
                <input type="text" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Email Address</label>
                <input type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Phone Number</label>
                <input type="text" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Role</label>
                <input type="text" value={profile.role} readOnly className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Timezone</label>
                <select value={profile.timezone} onChange={e => setProfile(p => ({ ...p, timezone: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                  <option value="Asia/Kolkata">Asia/Kolkata (IST +5:30)</option>
                  <option value="Asia/Dubai">Asia/Dubai (GST +4:00)</option>
                  <option value="UTC">UTC (+0:00)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Language</label>
                <select value={profile.language} onChange={e => setProfile(p => ({ ...p, language: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Marathi">Marathi</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Bio</label>
                <textarea rows={3} value={profile.bio} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50 resize-none" />
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={handleSaveProfile} disabled={loadingProfile} className="px-6 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2">
                {loadingProfile && <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
                {loadingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

            {/* Recent Activity */}
            <div className="pt-4 border-t border-background-200/60">
              <h4 className="text-sm font-semibold text-foreground-950 mb-3">Recent Activity</h4>
              <div className="space-y-2">
                {recentActivity.map(act => (
                  <div key={act.id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-background-50">
                    <div className="w-2 h-2 rounded-full bg-accent-500 mt-1.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-medium text-foreground-950">{act.action}</p>
                        <span className="text-[10px] text-foreground-500">·</span>
                        <p className="text-[11px] text-foreground-500 truncate">{act.detail}</p>
                      </div>
                      <p className="text-[10px] text-foreground-500 mt-0.5">{act.time} · {act.device} · {act.ip}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-4">
            {/* Change Password */}
            <div className="rounded-xl border border-background-200/60 bg-white p-6 space-y-4">
              <h3 className="text-sm font-semibold text-foreground-950">Change Password</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Current Password</label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                    <input type={showCurrentPwd ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    <button type="button" onClick={() => setShowCurrentPwd(!showCurrentPwd)} className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer text-foreground-400 hover:text-foreground-600 transition-colors" tabIndex={-1}>
                      <i className={showCurrentPwd ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">New Password</label>
                  <div className="relative">
                    <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                    <input type={showNewPwd ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 8 characters" className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
                    <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer text-foreground-400 hover:text-foreground-600 transition-colors" tabIndex={-1}>
                      <i className={showNewPwd ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                  {newPassword && (
                    <div className="mt-2 space-y-1">
                      <div className="flex gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className={`h-1 flex-1 rounded-full ${i < pwdStrength.score ? pwdStrength.color : 'bg-background-200/60'}`} />
                        ))}
                      </div>
                      <p className="text-[10px] text-foreground-500">Strength: <span className={`font-semibold ${pwdStrength.score >= 4 ? 'text-teal-400' : pwdStrength.score >= 3 ? 'text-accent-700' : 'text-rose-400'}`}>{pwdStrength.label}</span></p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Confirm New Password</label>
                  <div className="relative">
                    <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className={`w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none ${confirmPassword && confirmPassword !== newPassword ? 'border-rose-400 focus:border-rose-400' : 'border-background-200/60 focus:border-accent-500/50'}`} />
                    {confirmPassword && confirmPassword === newPassword && <i className="ri-check-line absolute right-3.5 top-1/2 -translate-y-1/2 text-teal-400" />}
                  </div>
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="text-[11px] text-rose-400 mt-1">Passwords do not match</p>
                  )}
                </div>
                <button onClick={handleChangePassword} disabled={loadingPassword} className="w-full py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  {loadingPassword && <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
                  {loadingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>

            {/* 2FA */}
            <div className="rounded-xl border border-background-200/60 bg-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950">Two-Factor Authentication</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">Add an extra layer of security to your account</p>
                </div>
                <button
                  onClick={() => {
                    setTwoFAEnabled(!twoFAEnabled);
                    setActionMsg({ type: 'success', text: `2FA ${!twoFAEnabled ? 'enabled' : 'disabled'}.` });
                    clearMsg();
                  }}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${twoFAEnabled ? 'bg-accent-500' : 'bg-background-200/50'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${twoFAEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {twoFAEnabled && (
                <div className="mt-4 p-3 rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center gap-2">
                  <i className="ri-shield-check-fill text-teal-400" />
                  <p className="text-xs text-teal-400">Two-factor authentication is enabled. Authenticator app configured.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-4">
            {/* Channel Configuration */}
            <div className="rounded-xl border border-background-200/60 bg-white p-6">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">Delivery Channels</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-4 rounded-xl bg-background-50 border border-background-200/40 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-notification-3-line" /></div>
                    <span className="text-sm font-semibold text-foreground-950">In-App</span>
                  </div>
                  <p className="text-[11px] text-foreground-500">Real-time notifications in dashboard</p>
                  <div className="flex items-center gap-1.5 pt-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                    <span className="text-[10px] text-teal-400 font-medium">Always active</span>
                  </div>
                </div>
                <div className={`p-4 rounded-xl border transition-colors ${notifPrefs.emailAlerts ? 'bg-background-50 border-accent-200/50' : 'bg-white border-background-200/40'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-mail-line" /></div>
                      <span className="text-sm font-semibold text-foreground-950">Email</span>
                    </div>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, emailAlerts: !p.emailAlerts }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.emailAlerts ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.emailAlerts ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <p className="text-[11px] text-foreground-500">Send to {profile.email || 'admin@cashmyproperty.ai'}</p>
                </div>
                <div className={`p-4 rounded-xl border transition-colors ${notifPrefs.smsAlerts ? 'bg-background-50 border-accent-200/50' : 'bg-white border-background-200/40'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-smartphone-line" /></div>
                      <span className="text-sm font-semibold text-foreground-950">SMS</span>
                    </div>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, smsAlerts: !p.smsAlerts }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.smsAlerts ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.smsAlerts ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <p className="text-[11px] text-foreground-500">Send to {profile.phone || '+91-9999999999'}</p>
                </div>
              </div>
            </div>

            {/* Event Category: Leads */}
            <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-background-200/60 flex items-center gap-2 bg-background-50">
                <div className="w-7 h-7 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-user-search-line text-xs" /></div>
                <span className="text-sm font-semibold text-foreground-950">Lead Notifications</span>
                <span className="text-[10px] text-foreground-500 ml-auto">{[notifPrefs.newLead, notifPrefs.leadStatusChange, notifPrefs.leadScoreUpdate, notifPrefs.leadAssignment].filter(Boolean).length}/4 active</span>
              </div>
              <div className="divide-y divide-background-200/50">
                {/* New Lead */}
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-green-500/15 text-green-400 flex items-center justify-center flex-shrink-0"><i className="ri-user-add-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">New Lead Received</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When someone submits a contact form or inquiry</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.newLeadEmail} onChange={() => setNotifPrefs(p => ({ ...p, newLeadEmail: !p.newLeadEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.newLeadSMS} onChange={() => setNotifPrefs(p => ({ ...p, newLeadSMS: !p.newLeadSMS }))} disabled={!notifPrefs.smsAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.smsAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>SMS</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, newLead: !p.newLead, newLeadEmail: !p.newLead ? true : p.newLeadEmail, newLeadSMS: !p.newLead ? true : p.newLeadSMS }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.newLead ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.newLead ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {/* Lead Status Change */}
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0"><i className="ri-arrow-left-right-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">Lead Status Changed</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When a lead moves to a new pipeline stage</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.leadStatusChangeEmail} onChange={() => setNotifPrefs(p => ({ ...p, leadStatusChangeEmail: !p.leadStatusChangeEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.leadStatusChangeSMS} onChange={() => setNotifPrefs(p => ({ ...p, leadStatusChangeSMS: !p.leadStatusChangeSMS }))} disabled={!notifPrefs.smsAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.smsAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>SMS</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, leadStatusChange: !p.leadStatusChange }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.leadStatusChange ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.leadStatusChange ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {/* Lead Score Update */}
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0"><i className="ri-bar-chart-2-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">Lead Score Updated</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When the AI recalculates a lead's score</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.leadScoreUpdateEmail} onChange={() => setNotifPrefs(p => ({ ...p, leadScoreUpdateEmail: !p.leadScoreUpdateEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.leadScoreUpdateSMS} onChange={() => setNotifPrefs(p => ({ ...p, leadScoreUpdateSMS: !p.leadScoreUpdateSMS }))} disabled={!notifPrefs.smsAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.smsAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>SMS</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, leadScoreUpdate: !p.leadScoreUpdate }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.leadScoreUpdate ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.leadScoreUpdate ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {/* Lead Assignment */}
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0"><i className="ri-user-shared-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">Lead Assigned / Reassigned</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When a lead is assigned to a relationship manager</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.leadAssignmentEmail} onChange={() => setNotifPrefs(p => ({ ...p, leadAssignmentEmail: !p.leadAssignmentEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.leadAssignmentSMS} onChange={() => setNotifPrefs(p => ({ ...p, leadAssignmentSMS: !p.leadAssignmentSMS }))} disabled={!notifPrefs.smsAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.smsAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>SMS</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, leadAssignment: !p.leadAssignment }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.leadAssignment ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.leadAssignment ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Event Category: Deals */}
            <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-background-200/60 flex items-center gap-2 bg-background-50">
                <div className="w-7 h-7 rounded-lg bg-green-500/15 text-green-400 flex items-center justify-center"><i className="ri-exchange-funds-line text-xs" /></div>
                <span className="text-sm font-semibold text-foreground-950">Deal Notifications</span>
                <span className="text-[10px] text-foreground-500 ml-auto">{[notifPrefs.dealStageChange, notifPrefs.dealClosed].filter(Boolean).length}/2 active</span>
              </div>
              <div className="divide-y divide-background-200/50">
                {/* Deal Stage Change */}
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0"><i className="ri-git-branch-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">Deal Stage Changed</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When a deal advances or regresses in the pipeline</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.dealStageChangeEmail} onChange={() => setNotifPrefs(p => ({ ...p, dealStageChangeEmail: !p.dealStageChangeEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, dealStageChange: !p.dealStageChange }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.dealStageChange ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.dealStageChange ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {/* Deal Closed */}
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-green-500/15 text-green-400 flex items-center justify-center flex-shrink-0"><i className="ri-trophy-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">Deal Closed (Won / Lost)</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When a deal is finalized or lost</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.dealClosedEmail} onChange={() => setNotifPrefs(p => ({ ...p, dealClosedEmail: !p.dealClosedEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.dealClosedSMS} onChange={() => setNotifPrefs(p => ({ ...p, dealClosedSMS: !p.dealClosedSMS }))} disabled={!notifPrefs.smsAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.smsAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>SMS</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, dealClosed: !p.dealClosed }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.dealClosed ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.dealClosed ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Event Category: Properties */}
            <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-background-200/60 flex items-center gap-2 bg-background-50">
                <div className="w-7 h-7 rounded-lg bg-teal-500/15 text-teal-400 flex items-center justify-center"><i className="ri-home-5-line text-xs" /></div>
                <span className="text-sm font-semibold text-foreground-950">Property Notifications</span>
                <span className="text-[10px] text-foreground-500 ml-auto">{[notifPrefs.propertyListed, notifPrefs.propertyStatusChange].filter(Boolean).length}/2 active</span>
              </div>
              <div className="divide-y divide-background-200/50">
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-teal-500/15 text-teal-400 flex items-center justify-center flex-shrink-0"><i className="ri-building-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">New Property Listed</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When a new property is added to the marketplace</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.propertyListedEmail} onChange={() => setNotifPrefs(p => ({ ...p, propertyListedEmail: !p.propertyListedEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, propertyListed: !p.propertyListed }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.propertyListed ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.propertyListed ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0"><i className="ri-toggle-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">Property Status Changed</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When a property is rented, sold, or marked inactive</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.propertyStatusChangeEmail} onChange={() => setNotifPrefs(p => ({ ...p, propertyStatusChangeEmail: !p.propertyStatusChangeEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, propertyStatusChange: !p.propertyStatusChange }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.propertyStatusChange ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.propertyStatusChange ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Event Category: System & Reports */}
            <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-background-200/60 flex items-center gap-2 bg-background-50">
                <div className="w-7 h-7 rounded-lg bg-rose-500/15 text-rose-400 flex items-center justify-center"><i className="ri-settings-3-line text-xs" /></div>
                <span className="text-sm font-semibold text-foreground-950">System & Reports</span>
                <span className="text-[10px] text-foreground-500 ml-auto">{[notifPrefs.systemAlerts, notifPrefs.reportReady, notifPrefs.weeklyDigest].filter(Boolean).length}/3 active</span>
              </div>
              <div className="divide-y divide-background-200/50">
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-rose-500/15 text-rose-400 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">System & Security Alerts</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">Critical platform issues, login alerts, and security events</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.systemAlertsEmail} onChange={() => setNotifPrefs(p => ({ ...p, systemAlertsEmail: !p.systemAlertsEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.systemAlertsSMS} onChange={() => setNotifPrefs(p => ({ ...p, systemAlertsSMS: !p.systemAlertsSMS }))} disabled={!notifPrefs.smsAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.smsAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>SMS</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, systemAlerts: !p.systemAlerts }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.systemAlerts ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.systemAlerts ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0"><i className="ri-file-chart-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">Feasibility Report Ready</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">When an AI-generated analysis report is completed</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.reportReadyEmail} onChange={() => setNotifPrefs(p => ({ ...p, reportReadyEmail: !p.reportReadyEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, reportReady: !p.reportReady }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.reportReady ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.reportReady ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0"><i className="ri-calendar-check-line text-[10px]" /></span>
                      <p className="text-sm text-foreground-950">Weekly Digest</p>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">Summary of all activity every Monday at 9 AM</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={notifPrefs.weeklyDigestEmail} onChange={() => setNotifPrefs(p => ({ ...p, weeklyDigestEmail: !p.weeklyDigestEmail }))} disabled={!notifPrefs.emailAlerts} className="w-3.5 h-3.5 rounded cursor-pointer disabled:opacity-30" />
                      <span className={`text-[10px] font-medium ${notifPrefs.emailAlerts ? 'text-foreground-500' : 'text-foreground-400'}`}>Email</span>
                    </label>
                    <button onClick={() => setNotifPrefs(p => ({ ...p, weeklyDigest: !p.weeklyDigest }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.weeklyDigest ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.weeklyDigest ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quiet Hours */}
            <div className="rounded-xl border border-background-200/60 bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-moon-line text-foreground-500" /> Quiet Hours
                  </h3>
                  <p className="text-[11px] text-foreground-500 mt-0.5">Pause notifications during specific hours</p>
                </div>
                <button onClick={() => setNotifPrefs(p => ({ ...p, quietHoursEnabled: !p.quietHoursEnabled }))} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${notifPrefs.quietHoursEnabled ? 'bg-accent-500' : 'bg-background-200/50'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs.quietHoursEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {notifPrefs.quietHoursEnabled && (
                <div className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/40">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500">From</span>
                    <select value={notifPrefs.quietHoursStart} onChange={e => setNotifPrefs(p => ({ ...p, quietHoursStart: e.target.value }))} className="px-3 py-1.5 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer">
                      {['21:00', '22:00', '23:00', '00:00'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500">To</span>
                    <select value={notifPrefs.quietHoursEnd} onChange={e => setNotifPrefs(p => ({ ...p, quietHoursEnd: e.target.value }))} className="px-3 py-1.5 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer">
                      {['06:00', '07:00', '08:00', '09:00'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <span className="text-[10px] text-foreground-500">IST</span>
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <button onClick={handleSaveNotifications} disabled={loadingNotifs} className="px-8 py-3 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap shadow-sm disabled:opacity-50 flex items-center gap-2">
                {loadingNotifs && <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
                {loadingNotifs ? 'Saving...' : 'Save Notification Preferences'}
              </button>
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200/60 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-950">Active Sessions</h3>
              <button
                onClick={handleSignOutAll}
                className="text-xs text-rose-400 hover:text-rose-300 cursor-pointer whitespace-nowrap"
              >
                Sign Out All Sessions
              </button>
            </div>
            <div className="divide-y divide-background-200/50">
              {activeSessions.map(session => (
                <div key={session.id} className={`flex items-center gap-4 px-5 py-4 ${session.current ? 'bg-teal-500/5' : ''}`}>
                  <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className={`text-foreground-500 text-lg ${session.device.includes('iPhone') ? 'ri-smartphone-line' : session.device.includes('Safari') ? 'ri-apple-line' : 'ri-computer-line'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground-950">{session.device}</p>
                      {session.current && <span className="text-[9px] font-semibold text-teal-400 bg-teal-500/10 px-1.5 py-0.5 rounded-full">Current</span>}
                    </div>
                    <p className="text-[11px] text-foreground-500">{session.location} · {session.ip}</p>
                    <p className="text-[10px] text-foreground-500">{session.time}</p>
                  </div>
                  {!session.current && (
                    <button
                      onClick={() => handleRevokeSession(session.id)}
                      className="px-3 py-1.5 rounded-lg border border-rose-500/20 text-xs text-rose-400 hover:bg-rose-500/10 cursor-pointer whitespace-nowrap"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-background-200/60 bg-background-50">
              <p className="text-[11px] text-foreground-500">Sessions automatically expire after 30 days of inactivity. Revoking a session will immediately sign out that device.</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}